/**
 * POI Distance Service
 * 
 * Provides utilities for calculating POI positions relative to routes
 */

import type { POI, RouteData } from '@cya-routes/shared'

export interface POIDistanceResult {
  distance: number
  isOffRoute: boolean
  offRouteDistance: number
}

/**
 * Calculate POI distance and position along a route
 * @param poi - POI with coordinates
 * @param route - Route data with coordinates and elevation profile
 * @returns Object with distance along route, off-route status, and off-route distance
 */
export const calculatePOIDistance = (poi: POI, route: RouteData): POIDistanceResult => {
  if (!poi.coordinates || !route.coordinates || !route.elevation?.profile) {
    return { distance: 0, isOffRoute: true, offRouteDistance: 0 }
  }

  const routeCoordinates = route.coordinates
  const elevationData = route.elevation.profile
  
  let closestDistance = Infinity
  let closestRouteDistance = 0

  routeCoordinates.forEach((coord, index) => {
    const [routeLng, routeLat] = coord
    const distance = Math.sqrt(
      Math.pow(routeLat - poi.coordinates!.lat, 2) + 
      Math.pow(routeLng - poi.coordinates!.lng, 2)
    )

    if (distance < closestDistance) {
      closestDistance = distance
      // Find corresponding elevation data point
      const elevationPoint = elevationData[index]
      if (elevationPoint) {
        closestRouteDistance = elevationPoint.distance
      }
    }
  })

  // Convert degrees to approximate meters (rough approximation)
  const distanceInMeters = closestDistance * 111000
  const isOffRoute = distanceInMeters > 3000 // 3km threshold

  return {
    distance: closestRouteDistance,
    isOffRoute,
    offRouteDistance: distanceInMeters / 1000 // Convert to km
  }
}

/**
 * Calculate POI distances for multiple POIs along a route
 * @param pois - Array of POIs
 * @param route - Route data
 * @returns Array of POI distance results
 */
export const calculateMultiplePOIDistances = (pois: POI[], route: RouteData): (POIDistanceResult & { poi: POI })[] => {
  return pois.map(poi => ({
    poi,
    ...calculatePOIDistance(poi, route)
  }))
}

/**
 * Filter POIs by their proximity to route
 * @param pois - Array of POIs
 * @param route - Route data
 * @param maxOffRouteDistance - Maximum off-route distance in km (default: 3)
 * @returns Array of POIs within the specified distance
 */
export const filterPOIsByProximity = (pois: POI[], route: RouteData, maxOffRouteDistance: number = 3): POI[] => {
  return pois.filter(poi => {
    const result = calculatePOIDistance(poi, route)
    return !result.isOffRoute || result.offRouteDistance <= maxOffRouteDistance
  })
}